import React, { ChangeEvent } from "react";
import { useState, useEffect, useFormContext, Hooks, Form, Field, Name, Title, Info, Code, Actions, Toast,  API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { Estimate } from "models/EstimateModel";
import { Item, emptyItem } from "models/ItemModel";
import AsyncSelect from "react-select/async";
import { Calculate } from "helpers/CalculateHelpers"
import { useSearchParams } from 'react-router-dom';
import { Inventory } from "models/InventoryModel";
import { Product } from "models/ProductModel";

const EstimateForm: React.FC = () => {
  const width = { product: 24, price: 10, qty: 10, unit: 12, gross: 8, discountP: 6, discountA: 8, depositP: 6, depositA: 8, taxable: 8, taxP:6, taxA:8, netAmount: 12 }
  const [formId, formName] = [Code.Form.Id.ESTIMATE, Code.Form.Name.ESTIMATE];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [doctorOptions, setDoctorOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Estimate>({});
  const [unitOptions, setUnitOptions] = Hooks.Form.Options();
  const [segmentOptions, setSegmentOptions] = Hooks.Form.Options();
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [termOptions, setTermOptions] = Hooks.Form.Options();
  const [allTermOptions, setAllTermOptions] = Hooks.Form.OptionMore();
  const [selectedProduct, setSelectedProduct] = useState<OptionType | null>(null);
  const emptyOptions = Utility.Get.OptionValues([Code.Option.Common.NA]);
  const [localSkucode, setLocalSkucode] = useState<string>('');

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);

  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
   
  useEffect(() => {

    Files.Json.Http(Code.File.UNIT, Code.Option.Common.NOTHING, setUnitOptions, setError);
    Files.Json.API(Code.File.DOCTOR, Code.Option.Common.NA, setDoctorOptions, setError);
    Files.Json.API(Code.File.SEGMENT, Code.Option.Common.SELECT, setSegmentOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.ESTIMATE_STATUS, Code.Option.Common.NA, setStatusOptions, setError);
    Files.Json.API(Code.File.TERM, Code.Option.Common.NA, setAllTermOptions, setError);
    
    API.Data.GetByKeyId<Estimate>(formName, keyId, setLoading, setError)
    .then(async(data) => { if(data) {
      setActiveId(data.estimate_active); 
      const clientData = await API.Data.getClientNameNumber(data.client_id);
      const formData = await Form.Map.Data(data);
      setFormData((prevData) => ({
        ...prevData,
        ...formData,
        ...clientData, 
      }));
      setLoading(false);
    }})

    const onNewEntry = async () => {
      if(keyId===Code.Key.NEW) {
        if(clientId){
          const clientName = await API.Data.GetClientName(Number(clientId));
          setFormData((prevData) => ({
            ...prevData,
            client_id: Number(clientId),
            client_name: Utility.Value.Text(clientName), 
          }));
        }
        setFormData((prev) => ({...prev, segment_id: Code.Option.Segment.OPTICAL}));
      }
      setLoading(false);
    };
    onNewEntry();
  }, []);

  useEffect(() => {
    if(allTermOptions){
      const filteredTerms = allTermOptions.filter(option => option.data1 === formId.toString());
      if(filteredTerms.length > 0) setTermOptions(filteredTerms); else setTermOptions(emptyOptions);
    }
  }, [allTermOptions]);

  const onItemChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    const isCalculate = Code.Item.NoneCalculateFields.includes(name);
    const updatedValue = isCalculate ? value : (value ? Number(value) || 0 : 0);
  
    setFormData((prevData) => {
      const updatedItems = [...prevData.items!];
      const key = name as keyof Item;
  
      if (name === 'selected') {
        updatedItems[index] = {
          ...updatedItems[index],
          [key]: checked,
          item_active: checked ? 3 : 2, 
        };
      } else {
        updatedItems[index] = {
          ...updatedItems[index],
          [key]: updatedValue,
        };
      }
  
      updatedItems[index] = Calculate.ItemValues(updatedItems[index]);
      const grandTotals = Calculate.GrandTotals(updatedItems,formName);
  
      return {
        ...prevData,
        items: updatedItems,
        estimate_gross_amount: grandTotals.estimate_gross_amount || 0,
        estimate_discount_amount: grandTotals.estimate_discount_amount || 0,
        estimate_deposit_amount: grandTotals.estimate_deposit_amount || 0,
        estimate_taxable_amount: grandTotals.estimate_taxable_amount || 0,
        estimate_tax_amount: grandTotals.estimate_tax_amount || 0,
        estimate_net_amount: grandTotals.estimate_net_amount || 0,
        estimate_payable_amount: grandTotals.estimate_payable_amount || 0,
      };
    });
  };

  const addItem = async () => {
    
    if (localSkucode || (selectedProduct?.value  && selectedProduct?.value > 0) ) {    
    
      let productId = selectedProduct?.value || 0;
      let productName = selectedProduct?.label || '';
      let specificationId = 2;
      let productComment = '';
      let utnCode = ''
      let skuCode = ''
      let inventoryId = 2;
      let salePrice = 0;
      let quantity = 1;
      let discountPercent = 0;
      let discountAmount = 0;
      let taxPercent = 0;

      const fields = Utility.Join.Fields([
        Name.Inventory.SKU,
        Name.Inventory.UTN,
        Name.Inventory.SerialNo,
        Name.Inventory.Sale,
        Name.Inventory.Data,
        Name.Product.Id,
        Name.Specification.Id,
      ]);

      if(productId == 0){
        const inventoryData = await API.Data.GetInventoryViewById<Inventory>(localSkucode);
        if(inventoryData){
          utnCode = inventoryData.inventory_utncode || '';
          skuCode = inventoryData.inventory_skucode || '';
          specificationId = inventoryData.specification_id || 2;
          productId = inventoryData.product_id || productId;
          inventoryId = inventoryData.inventory_id || inventoryId;
          salePrice = inventoryData.inventory_sale_price || 0;
          productComment = Utility.Get.ProductSummary(inventoryData.inventory_data, specificationId);
          productName = inventoryData.product_name || productName;
          salePrice = inventoryData.product_sale_price || 0;
          discountPercent = inventoryData.offer_percentage || 0;
          discountAmount = inventoryData.offer_amount || 0;
          taxPercent = inventoryData.tax_percentage || 0;
        }
        else { Utility.Message.Warning(["No inventory found for the entered SKU/UTN"],setMessage); return; }
      }
      else
      {
        const productData = await API.Data.GetProductViewById<Product>(productId);
        if(productData){
          productName = productData.product_name || productName;
          productComment = productData.product_comment || '';
          salePrice = productData.product_sale_price || 0;
          discountPercent = productData.offer_percentage || 0;
          discountAmount = productData.offer_amount || 0;
          taxPercent = productData.tax_percentage || 0;
          quantity = productData.product_min_sale || 1;
        }
        else{ Utility.Message.Warning(["Product not found"],setMessage); return; }
      }

      const newItem: Item = { 
        ...emptyItem, 
        item_utncode: utnCode,
        item_skucode: skuCode,
        inventory_id: inventoryId,
        item_price: Number(salePrice),
        item_quantity: quantity,
        item_net_amount: Number(salePrice * quantity),
        ...(productId !== 0 && {
          product_id: productId,
          product_name: productName,
        }),
        item_comment: productComment,
        stock_status_id: 1,  
        item_discount_percentage: Number(discountPercent),
        item_discount_amount: Number(discountAmount),
        item_tax1_percentage: Number(taxPercent),
      };
      
      setFormData((prev) => {
        const updatedItems = [...(prev.items || []), newItem];
        const newIndex = updatedItems.length - 1;

        const fakeEvent = {
          target: { name: "item_price", value: salePrice, type: "number", checked: false },
        } as unknown as React.ChangeEvent<HTMLInputElement>;

        const updatedItem = Calculate.ItemValues(updatedItems[newIndex]);
        updatedItems[newIndex] = updatedItem;

        const grandTotals = Calculate.GrandTotals(updatedItems, formName);

        //console.log("Grand Totals after adding item:", grandTotals);

        return {
          ...prev,
          items: updatedItems,
          estimate_gross_amount: grandTotals.estimate_gross_amount || 0,
          estimate_discount_amount: grandTotals.estimate_discount_amount || 0,
          estimate_deposit_amount: grandTotals.estimate_deposit_amount || 0,
          estimate_taxable_amount: grandTotals.estimate_taxable_amount || 0,
          estimate_tax_amount: grandTotals.estimate_tax_amount || 0,
          estimate_net_amount: grandTotals.estimate_net_amount || 0,
          estimate_payable_amount: grandTotals.estimate_payable_amount || 0,
        };
      });
      

      // TODO: handle inventoryData (set state, update form, etc.)
      //console.log("formData------>",formData);
      setSelectedProduct({value: 0, label: 'Select'});
      setLocalSkucode('');
    }
  };


  const calculateFunction = (updatedFormData: any) => {
    let roundOffAmount = Number(updatedFormData.estimate_roundoff_amount) || 0;
    let netAmount = Number(updatedFormData.estimate_net_amount) || 0;
    let payableAmount = Number(updatedFormData.estimate_payable_amount) || 0;
    let insurer1Amount = Number(updatedFormData.estimate_insurer1_amount) || 0;
    let insurer2Amount = Number(updatedFormData.estimate_insurer2_amount) || 0;

    payableAmount = netAmount + roundOffAmount;
    let clientAmount = payableAmount - (insurer1Amount + insurer2Amount);

    return {
      ...updatedFormData,
      estimate_payable_amount: Utility.Maths.toDecimal(payableAmount),
      estimate_client_amount: Utility.Maths.toDecimal(clientAmount),
    };
  };
  
  useEffect(() => {
    setFormData((prev) => calculateFunction(prev));
  }, [ formData.estimate_net_amount, formData.estimate_roundoff_amount, formData.estimate_payable_amount ]);

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    const addonData = await API.Data.AddonData(formData, formName, keyId);
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
    });
    
    API.Data.Save(formName, keyId, finalData, setMessage)
  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} formName={formName} formId={formId} Privilege={Privilege}  print={true} />
          <Control.Tab.Header titles={["Details", "More"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
            
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Date id={Name.Estimate.Date} value={formData.estimate_date} title={Title.Date} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,20,1]} require={true} currentDate={true} />
                <Field.Text id={Name.Estimate.Number} value={formData.estimate_number} title={Title.SNo} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,20,1]} />
                <Field.FilterSelect id={Name.Doctor.Id} value={formData.doctor_id} title={Title.Doctor} options={doctorOptions} setFormData={setFormData} size={[1,20,1]}  />
                <Field.FilterSelect id={Name.Common.SegmentId} value={formData.segment_id} title={Title.Segment} options={segmentOptions} setFormData={setFormData} require={true} size={[1,20,1]} defaultValue={Code.Option.Segment.OPTICAL} />
                <Field.FilterSelect id={Name.Common.StatusId} value={formData.status_id} title={Title.Status} options={statusOptions} setFormData={setFormData} size={[1,20,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Header id={Name.Client.Id} title={Title.Client} require={true}>
                  <AsyncSelect id={Name.Client.Id} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.FormOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" />
                </Field.Header>
              </Control.Row>
              <Control.Row>
                <Field.Header id={Name.Product.Id} title={Title.Product} size={[0,40,1]}>
                  <AsyncSelect id={Name.Product.Id} value={selectedProduct} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.PRODUCT, Code.Mapping.product.value, Code.Mapping.product.label)} styles={Code.Style.FormOption} onChange={(selected) => setSelectedProduct(selected as OptionType | null)} placeholder="Search" />
                </Field.Header>
                <Field.Text id={Name.Item.SKU} value={localSkucode} title={Title.Skucode} onChange={(e) => setLocalSkucode(e.target.value)} size={[1,20,1]} />
                <Button.FrameAction title="+" info={Info.AddProduct} onClick={addItem} size={[1,20,1]} className='pt6' />
              </Control.Row>
              <Control.Form.Table>
                <Control.Form.Title>
                  <Control.Grid.Items values={[
                    [Title.Product, width.product], 
                    [Title.Price, width.price, Code.Align.RIGHT], 
                    [Title.Qty, width.qty, Code.Align.RIGHT],
                    [Title.Unit, width.unit],
                    [Title.Gross, width.gross, Code.Align.RIGHT, false],
                    [Title.Discount, width.discountP + width.depositA, Code.Align.CENTER],
                    [Title.Deposit, width.depositP + width.depositA, Code.Align.CENTER, false],
                    [Title.Taxable, width.taxable, Code.Align.RIGHT, false],
                    [Title.Tax, width.taxP + width.taxA, Code.Align.CENTER],
                    [Title.NetAmount, width.netAmount, Code.Align.RIGHT]
                  ]} first={1} />
                </Control.Form.Title>
                <Control.Form.List>
                  {formData.items?.map((item, index) => (
                    <Control.Form.Index key={index} index={index}>
                      <Control.Form.Item>
                        <Control.Form.Check index={index} checked={item.selected} handleItemChange={(index, e) => onItemChange(index, e)} />
                        <Control.Form.Text id={Name.Product.Name} value={Utility.Format.ProductQuick(item.product_name,item.item_skucode,item.item_utncode)} size={[width.product]} disabled={true} />
                        <Control.Form.Number id={Name.Item.Price} value={item.item_price} size={[width.price]} onChange={(e) => onItemChange(index,e)}  />
                        <Control.Form.Number id={Name.Item.Quantity} value={item.item_quantity} size={[width.qty]} onChange={(e) => onItemChange(index,e)}  />
                        <Control.Form.Option id={Name.Unit.Id} value={item.unit_id} optionValues={unitOptions} size={[width.unit]} onChange={(e) => onItemChange(index,e)}  />
                        <Control.Form.Number id={Name.Item.Gross} value={item.item_gross_amount} size={[width.gross]} onChange={(e) => onItemChange(index,e)} visible={false}  />
                        <Control.Form.Number id={Name.Item.DiscountP} value={item.item_discount_percentage} size={[width.discountP]} onChange={(e) => onItemChange(index,e)} placeholder="%" />
                        <Control.Form.Number id={Name.Item.DiscountA} value={item.item_discount_amount} size={[width.discountA]} onChange={(e) => onItemChange(index,e)}  />
                        <Control.Form.Number id={Name.Item.DepositP} value={item.item_deposit_percentage} size={[width.depositP]} onChange={(e) => onItemChange(index,e)} visible={false} placeholder="%" />
                        <Control.Form.Number id={Name.Item.DepositA} value={item.item_deposit_amount} size={[width.depositA]} onChange={(e) => onItemChange(index,e)} visible={false} />
                        <Control.Form.Number id={Name.Item.Taxable} value={item.item_taxable_amount} size={[width.taxable]} onChange={(e) => onItemChange(index,e)} visible={false} />
                        <Control.Form.Number id={Name.Item.Tax1P} value={item.item_tax1_percentage} size={[width.taxP]} onChange={(e) => onItemChange(index,e)} placeholder="%"  />
                        <Control.Form.Number id={Name.Item.Tax1A} value={item.item_tax1_amount} size={[width.taxA]} onChange={(e) => onItemChange(index,e)}  />
                        <Control.Form.Number id={Name.Item.Net} value={item.item_net_amount} size={[width.netAmount]} onChange={(e) => onItemChange(index,e)} /> 
                      </Control.Form.Item>
                      <Control.Form.Item>
                        <Control.Form.TextArea index={index} value={item.item_comment} handleItemChange={(index, e) => onItemChange(index, e)} />
                      </Control.Form.Item>
                    </Control.Form.Index>
                  ))}
                </Control.Form.List>
              </Control.Form.Table>
              <Control.Flex>
                <Control.Column size={[0,35,0,2]}>
                  <Control.Row>
                    <Field.Text value="Client" title="Payable by" size={[0,50,1]} disabled={true} />
                    <Field.Amount id={Name.Estimate.Client} value={formData.estimate_client_amount}  title="Amount" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true} disabled={true} />
                  </Control.Row>
                  <Control.Row>
                    <Field.FilterSelect id={Name.Common.insurer1Id} value={formData.insurer1_id} options={doctorOptions} setFormData={setFormData} size={[0,50,1]} />
                    <Field.Amount id={Name.Estimate.Insurer1} value={formData.estimate_insurer1_amount} onChange={(e) => Utility.Handle.Calculate(e,setFormData,calculateFunction)} size={[1,50,0]} />
                  </Control.Row>
                  <Control.Row>
                    <Field.FilterSelect id={Name.Common.insurer2Id} value={formData.insurer2_id} options={doctorOptions} setFormData={setFormData} size={[0,50,1]} />
                    <Field.Amount id={Name.Estimate.Insurer2} value={formData.estimate_insurer2_amount} onChange={(e) => Utility.Handle.Calculate(e,setFormData,calculateFunction)} size={[1,50,0]} />
                  </Control.Row>
                </Control.Column>
                <Control.Column size={[0,15,0,3]}><></></Control.Column>
                <Control.Column size={[0,50,0,1]}>
                  <Control.Row>
                    <Field.Title size={[0,32,1]} />
                    <Field.Amount id={Name.Estimate.Gross} value={formData.estimate_gross_amount} title={Title.Gross} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} require={true} />
                    <Field.Amount id={Name.Estimate.Discount} value={formData.estimate_discount_amount} title={Title.Discount} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,0]} require={true}  />
                    {/* <Field.Number id={Name.Estimate.DepositAmount} value={formData.estimate_deposit_amount} title={Title.Deposit} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,16,0]} require={true} /> */}
                  </Control.Row>
                  <Control.Row>
                    <Field.Title size={[0,32,1]} />
                    {/* <Field.Number id={Name.Estimate.TaxableAmount} value={formData.estimate_taxable_amount} title={Title.Taxable} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,16,1]} require={true} disabled={true} /> */}
                    <Field.Amount id={Name.Estimate.Tax} value={formData.estimate_tax_amount} title={Title.Tax} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} require={true}  />
                    <Field.Amount id={Name.Estimate.Net} value={formData.estimate_net_amount} title={Title.NetAmount} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,0]} require={true}  />
                  </Control.Row>
                  <Control.Row>
                    <Field.Title size={[0,32,1]} />
                    <Field.Amount id={Name.Estimate.Roundoff} value={formData.estimate_roundoff_amount} title={Title.Roundoff} onChange={(e) => Utility.Handle.Calculate(e,setFormData,calculateFunction)} size={[1,34,1]} />
                    <Field.Amount id={Name.Estimate.Payable} value={formData.estimate_payable_amount} title={Title.Payable} onChange={(e) => Utility.Handle.Calculate(e,setFormData,calculateFunction)} size={[1,34,0]} require={true}  />
                  </Control.Row>
                  {/* <Control.Row>
                    <Button.FrameAction keyId={keyId} title={'Generate Invoice'} info={Info.AddProduct} onClick={generateInvoice} align={Code.Align.RIGHT} />
                  </Control.Row> */}
                </Control.Column>
              </Control.Flex>
            </Control.Tab.Content>
          
            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              <Control.Row>
                <Field.Text id={Name.Estimate.PoNumber} value={formData.estimate_po_number} title={"PO No."} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,24,1]} />
                <Field.Text id={Name.Estimate.RefNo} value={formData.estimate_ref_number} title={Title.Refno} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,26,1]} />
                <Field.Date id={Name.Estimate.StartDate} value={formData.estimate_start_date} title={"Start Date"} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,26,1]} />
                <Field.Date id={Name.Estimate.EndDate} value={formData.estimate_end_date} title={"Return Date"} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,24,0]} />
              </Control.Row>
              <Control.Row>
                <Field.FilterSelect id={Name.Common.TermId} value={formData.term_id} title={"Terms & Conditions"} options={termOptions} setFormData={setFormData} size={[0,50,1]} />
                <Field.FilterSelect id={Name.Address.Id} value={formData.address_id} title={"Delivery Address"} options={emptyOptions} setFormData={setFormData} size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.Estimate.Comment} value={formData.estimate_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Area id={Name.Estimate.Comment} value={formData.estimate_comment} title={"New Delivery Address"} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.Estimate.PrintableNote} value={formData.estimate_printable_note} title={"Printable Notes"} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Area id={Name.Estimate.InternalNote} value={formData.estimate_internal_note} title={"Internal Note"} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
            </Control.Tab.Content>
          
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} print={true} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default EstimateForm;